/**
 * Loading matrix: missions by antennas, allocated minutes with heat classes,
 * conflict markers, SLA per mission and utilisation per antenna. Row heights
 * and the table font follow the density setting.
 *
 * Keyboard: the table's controls share one tab stop (a roving tabindex): the
 * arrow keys move between the antenna headers, the mission names and the
 * cells, Home and End go to the ends of the row, Enter or Space activates.
 * It stays a plain table, so screen readers keep their table navigation over
 * every cell, the SLA and utilisation figures included.
 */
import { useMemo, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useUI } from '@/state/store';
import { StatusIcon } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { getGlobeEngine } from '../../globe/engineHandle';
import { useLoader } from '../../store';
import { fmtMinutes, pct, slaTone, tierColor, utilTone } from '../format';

const PAGE = 60;

function heatClass(minutes: number, maxHoursPerDay: number, days: number): string {
  if (minutes <= 0) return '';
  const ratio = minutes / (maxHoursPerDay * 60 * days);
  if (ratio > 0.9) return 'bg-warning/40';
  if (ratio > 0.7) return 'bg-caution/35';
  if (ratio > 0.4) return 'bg-nominal/30';
  return 'bg-sim/20';
}

const TONE_TEXT = { nominal: 'text-nominal', caution: 'text-caution', warning: 'text-warning' } as const;

interface Pos {
  r: number;
  c: number;
}

/** Arrow-key movement over the grid's focusable controls, keyed by data-gr and data-gc. */
function moveInGrid(table: HTMLElement, from: Pos, key: string): HTMLElement | null {
  const items = [...table.querySelectorAll<HTMLElement>('[data-gr]')].map((el) => ({
    el,
    r: Number(el.dataset.gr),
    c: Number(el.dataset.gc),
  }));
  const sameRow = items.filter((i) => i.r === from.r).sort((a, b) => a.c - b.c);
  const sameCol = items.filter((i) => i.c === from.c).sort((a, b) => a.r - b.r);
  if (key === 'ArrowRight') return sameRow.find((i) => i.c > from.c)?.el ?? null;
  if (key === 'ArrowLeft') return [...sameRow].reverse().find((i) => i.c < from.c)?.el ?? null;
  if (key === 'ArrowDown') return sameCol.find((i) => i.r > from.r)?.el ?? null;
  if (key === 'ArrowUp') return [...sameCol].reverse().find((i) => i.r < from.r)?.el ?? null;
  if (key === 'Home') return sameRow[0]?.el ?? null;
  if (key === 'End') return sameRow[sameRow.length - 1]?.el ?? null;
  return null;
}

export function MatrixPanel() {
  const dataset = useLoader((s) => s.dataset);
  const results = useLoader((s) => s.results);
  const timeframe = useLoader((s) => s.timeframe);
  const activeMissionIds = useLoader((s) => s.activeMissionIds);
  const activeAntennaIds = useLoader((s) => s.activeAntennaIds);
  const selection = useUI((s) => s.selection);
  const select = useUI((s) => s.select);
  const [limit, setLimit] = useState(PAGE);
  const [active, setActive] = useState<Pos>({ r: 1, c: 0 });
  const tableRef = useRef<HTMLTableElement>(null);

  const model = useMemo(() => {
    if (!dataset || !results) return null;
    const activeA = new Set(activeAntennaIds);
    const activeM = new Set(activeMissionIds);
    const antennas = dataset.antennas.filter((a) => activeA.has(a.id));
    const missions = dataset.missions
      .filter((m) => activeM.has(m.id))
      .sort((a, b) => a.priorityTier - b.priorityTier || a.shortName.localeCompare(b.shortName));
    const conflictCells = new Set<string>();
    for (const c of results.conflicts) {
      conflictCells.add(`${c.missionA}|${c.antennaId}`);
      conflictCells.add(`${c.missionB}|${c.antennaId}`);
    }
    const days = Math.max(1 / 24, (timeframe.endMs - timeframe.startMs) / 86_400_000);
    return { antennas, missions, conflictCells, days };
  }, [dataset, results, activeAntennaIds, activeMissionIds, timeframe]);

  if (!dataset || !results || !model)
    return <div className="p-4 text-xs text-ink-3">Run the analysis to see the loading matrix.</div>;

  const { antennas, missions, conflictCells, days } = model;
  const shown = missions.slice(0, limit);
  /** Roving tab stop: the active position if it still exists, else the first mission. */
  const activeExists = (active.r === 0 && active.c >= 1 && active.c <= antennas.length) ||
    (active.r >= 1 && active.r <= shown.length && active.c >= 0 && active.c <= antennas.length);
  const stop: Pos = activeExists ? active : { r: 1, c: 0 };
  const grid = (r: number, c: number) => ({
    'data-gr': r,
    'data-gc': c,
    tabIndex: stop.r === r && stop.c === c ? 0 : -1,
  });

  const onKeyDown = (e: KeyboardEvent<HTMLTableElement>) => {
    const t = e.target as HTMLElement;
    if (t.dataset.gr === undefined || !tableRef.current) return;
    const next = moveInGrid(tableRef.current, { r: Number(t.dataset.gr), c: Number(t.dataset.gc) }, e.key);
    if (!next) return;
    e.preventDefault();
    next.focus();
  };
  const onFocus = (e: FocusEvent<HTMLTableElement>) => {
    const t = e.target as HTMLElement;
    if (t.dataset.gr === undefined) return;
    const pos = { r: Number(t.dataset.gr), c: Number(t.dataset.gc) };
    if (pos.r !== active.r || pos.c !== active.c) setActive(pos);
  };

  return (
    <div className="h-full overflow-auto text-data scrollbar-thin">
      <table
        ref={tableRef}
        aria-label="Loading matrix: allocated minutes per mission and antenna"
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        className="min-w-full border-separate border-spacing-0"
      >
        <thead className="sticky top-0 z-10 bg-raised">
          <tr>
            <th className="sticky left-0 z-20 border-b border-r border-hairline bg-raised px-cell-x py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-3">
              Mission
            </th>
            {antennas.map((a, i) => (
              <th
                key={a.id}
                className={cn(
                  'border-b border-hairline px-1 py-1.5 text-center font-mono text-data-sm font-normal',
                  selection.antennaId === a.id ? 'text-accent' : 'text-ink-2',
                )}
              >
                <button
                  type="button"
                  title={a.name}
                  aria-current={selection.antennaId === a.id ? 'true' : undefined}
                  onClick={() => select({ antennaId: a.id, missionId: null, assetId: null })}
                  className="rounded-control hover:text-accent focus-visible:outline-offset-0"
                  {...grid(0, i + 1)}
                >
                  {a.shortName}
                </button>
              </th>
            ))}
            <th className="border-b border-l border-hairline px-cell-x py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-ink-3">
              SLA
            </th>
          </tr>
        </thead>
        <tbody>
          {shown.map((m, row) => {
            const sla = results.missionSLA[m.id];
            const rowSelected = selection.missionId === m.id;
            const tone = sla ? slaTone(sla.status) : null;
            return (
              <tr key={m.id} className={cn(rowSelected && 'bg-accent-soft/30')}>
                <th
                  scope="row"
                  className={cn(
                    'sticky left-0 z-10 border-b border-r border-hairline bg-raised px-cell-x py-cell-y text-left font-normal',
                    rowSelected ? 'text-accent' : 'text-ink',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => select({ missionId: m.id, antennaId: null, assetId: null })}
                    aria-current={rowSelected ? 'true' : undefined}
                    className={cn('flex items-center gap-1.5 rounded-control hover:text-accent', rowSelected && 'font-semibold')}
                    title={`${m.name}, tier ${m.priorityTier}`}
                    {...grid(row + 1, 0)}
                  >
                    <span
                      aria-hidden
                      className="h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ background: tierColor(dataset.tiers, m.priorityTier) }}
                    />
                    <span className="truncate">{m.shortName}</span>
                  </button>
                </th>
                {antennas.map((a, col) => {
                  const minutes = sla?.perAntenna[a.id]?.minutes ?? 0;
                  const conflict = conflictCells.has(`${m.id}|${a.id}`);
                  const label =
                    minutes > 0
                      ? `${m.shortName} on ${a.shortName}: ${fmtMinutes(minutes)}, ${sla?.perAntenna[a.id]?.passes ?? 0} passes${conflict ? ', conflicts' : ''}`
                      : `${m.shortName} on ${a.shortName}: no contact`;
                  return (
                    <td
                      key={a.id}
                      className={cn(
                        'border-b border-hairline px-1 py-cell-y text-center font-mono',
                        heatClass(minutes, a.capabilities.maxHoursPerDay ?? 18, days),
                        minutes > 0 ? 'text-ink' : 'text-ink-3',
                      )}
                    >
                      <button
                        type="button"
                        title={label}
                        aria-label={label}
                        onClick={() => {
                          select({ missionId: m.id, antennaId: null, assetId: null });
                          getGlobeEngine()?.flyToAntenna(a.id);
                        }}
                        className="inline-flex w-full items-center justify-center gap-0.5 rounded-control hover:text-accent focus-visible:outline-offset-0"
                        {...grid(row + 1, col + 1)}
                      >
                        {minutes > 0 ? (
                          Math.round(minutes)
                        ) : (
                          <span aria-hidden className="inline-block h-1 w-1 rounded-full bg-ink-3" />
                        )}
                        {conflict && <AlertTriangle aria-hidden className="h-3 w-3 text-warning" />}
                      </button>
                    </td>
                  );
                })}
                <td className="border-b border-l border-hairline px-cell-x py-cell-y text-right">
                  {sla && tone ? (
                    <span
                      className={cn('inline-flex items-center justify-end gap-1 font-mono', TONE_TEXT[tone])}
                      title={`${fmtMinutes(sla.totalMinutes)} of ${fmtMinutes(sla.requiredMinutes)}`}
                    >
                      <StatusIcon tone={tone} label={sla.status} />
                      {pct(sla.percentage)}
                    </span>
                  ) : (
                    <span className="text-ink-3">n/a</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="sticky bottom-0 bg-raised">
          <tr>
            <th
              scope="row"
              className="sticky left-0 z-10 border-r border-t border-hairline bg-raised px-cell-x py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-3"
            >
              Utilisation
            </th>
            {antennas.map((a) => {
              const u = results.antennaUtil[a.id];
              const tone = u ? utilTone(u.status) : 'nominal';
              return (
                <td
                  key={a.id}
                  className={cn(
                    'border-t border-hairline px-1 py-1.5 text-center font-mono',
                    tone === 'warning' ? 'text-warning' : tone === 'caution' ? 'text-caution' : 'text-ink-2',
                  )}
                  title={
                    u ? `${fmtMinutes(u.scheduledMinutes)} of ${fmtMinutes(u.availableMinutes)}, ${u.status}` : undefined
                  }
                >
                  <span className="inline-flex items-center gap-0.5">
                    {u && tone !== 'nominal' && <StatusIcon tone={tone} label={u.status} />}
                    {u ? pct(u.percentage) : <span className="sr-only">no data</span>}
                  </span>
                </td>
              );
            })}
            <td className="border-l border-t border-hairline px-cell-x py-1.5 text-right font-mono text-ink">
              {pct(results.summary.overallSLA)}
            </td>
          </tr>
        </tfoot>
      </table>
      {missions.length > limit && (
        <div className="p-2">
          <button
            type="button"
            onClick={() => setLimit((l) => l + PAGE)}
            className="rounded-control border border-hairline px-2 py-1 text-ink-2 hover:text-ink"
          >
            Show {Math.min(PAGE, missions.length - limit)} more of {missions.length - limit} remaining
          </button>
        </div>
      )}
    </div>
  );
}
